import React, { useState } from 'react';

interface ResourcesInvestmentHubProps {
  onOpenContactModal?: () => void;
}

export const ResourcesInvestmentHub: React.FC<ResourcesInvestmentHubProps> = ({ onOpenContactModal }) => {
  const [activeTab, setActiveTab] = useState<'agrarian' | 'minerals' | 'partnerships'>('agrarian');

  const produceItems = [
    {
      title: 'Uzairue Royal Yam & Tuber Crops',
      icon: '🥔',
      yield: '120,000 Metric Tons / Year',
      description: 'Famed for giant white yam cultivation across Jattu, Afashio, and Ikabigbo valleys. Highly prized for pounded yam cuisine.',
      opportunities: 'Yam processing, starch extraction factories, export packaging.',
    },
    {
      title: 'Cassava & Garri Production',
      icon: '🌿',
      yield: '180,000 Metric Tons / Year',
      description: 'High-yield cassava tubers processed into premium Uzairue Garri, starch, and industrial cassava flour.',
      opportunities: 'Modern garri processing mills, ethanol production, animal feed processing.',
    },
    {
      title: 'Palm Oil, Kernel & Palm Wine',
      icon: '🌴',
      yield: '75,000 Barrels Annually',
      description: 'Ancient oil palm plantations providing unrefined and refined red palm oil, palm kernel cake, and ceremonial palm wine.',
      opportunities: 'Refinery equipment, soap & cosmetics manufacturing, bio-energy.',
    },
    {
      title: 'Cocoa & Cash Crop Orchards',
      icon: '🍫',
      yield: '35,000 Bags Premium Grade',
      description: 'Organic cocoa plantations cultivated along the rolling hills of Uzairue Kingdom, along with cashew nuts and timber.',
      opportunities: 'Cocoa processing plants, chocolate artisanal cottage units, cashew processing.',
    },
  ];

  const mineralDeposits = [
    {
      name: 'Kaolin Clay Deposits',
      icon: '⚪',
      reserveTag: 'High Purity Ceramic Grade',
      location: 'Ikabigbo & Jattu Reserves',
      description: 'Extensive deposits of bright white kaolin clay, tested for high brightness and plasticity, suitable for ceramics, paper coating, paint filler, and pharmaceutical grade uses.',
      applications: ['Ceramic Tiles & Sanitaryware', 'Paints & Pigments', 'Paper Manufacturing', 'Pharmaceutical Excipients'],
    },
    {
      name: 'Granite & Igneous Rock Formations',
      icon: '🪨',
      reserveTag: 'Proven Dimension Stone Reserve',
      location: 'Iyora & Aviele Hills',
      description: 'Massive granite outcrops with variegated color patterns, ideal for commercial stone crushing, road construction aggregates, polished granite slabs, and monuments.',
      applications: ['Highway Construction Aggregates', 'Polished Floor Tiles & Slabs', 'Railway Ballast', 'Heavy Civil Engineering'],
    },
    {
      name: 'Laterite Soil & Clay Formation',
      icon: '🧱',
      reserveTag: 'Rich Iron-Oxide Aggregate',
      location: 'Widespread Across Uzairue Sub-Basin',
      description: 'High-density lateritic clay deposits used for eco-friendly compressed earth bricks, road base compaction, and refractory brick kilns.',
      applications: ['Compressed Stabilized Earth Blocks (CSEB)', 'Road Base Foundations', 'Refractory Bricks & Potteries', 'Terracotta Crafts'],
    },
  ];

  return (
    <div className="space-y-10 pb-12">
      
      {/* Header Banner */}
      <section className="bg-gradient-to-r from-[#3D1E16] via-[#2A140E] to-[#3D1E16] text-white rounded-3xl border-2 border-[#D4AF37]/50 p-6 sm:p-10 shadow-2xl relative overflow-hidden">
        <div className="absolute right-0 top-0 opacity-10 pointer-events-none">
          <img src="https://res.cloudinary.com/uuovmrek/image/upload/uzairuelogo01" alt="Uzairue Seal" className="w-80 h-80 object-contain" />
        </div>

        <div className="relative z-10 max-w-3xl space-y-4">
          <div className="inline-flex items-center gap-2 px-3 py-1 bg-[#D4AF37]/20 border border-[#D4AF37]/40 rounded-full text-[#D4AF37] text-xs font-black uppercase tracking-widest">
            <span>🏛️ Economic Gateway</span>
            <span>•</span>
            <span>Kingdom Investments</span>
          </div>

          <h1 className="text-3xl sm:text-5xl font-serif font-black text-white leading-tight">
            Uzairue Resources & <span className="text-[#D4AF37]">Investment Hub</span>
          </h1>

          <p className="text-sm sm:text-base text-amber-100/90 leading-relaxed font-sans">
            Uzairue Kingdom is blessed with bountiful agrarian soil and rich solid mineral deposits explicitly celebrated on our Royal ERUE Seal—<strong>Laterite, Granite, and Kaolin Clay</strong>. We invite global investors, agribusiness partners, and development agencies to collaborate with our Kingdom.
          </p>

          {/* Sub Navigation Switcher */}
          <div className="flex flex-wrap gap-2 pt-2">
            <button
              onClick={() => setActiveTab('agrarian')}
              className={`px-4 py-2 rounded-xl text-xs font-black uppercase tracking-wider transition-all cursor-pointer flex items-center gap-2 ${
                activeTab === 'agrarian'
                  ? 'bg-[#D4AF37] text-[#3D1E16] shadow-lg border border-[#D4AF37]'
                  : 'bg-[#3D1E16] text-amber-200 hover:bg-[#9E472A]/40 border border-[#D4AF37]/30'
              }`}
            >
              <span>🌾</span>
              <span>The Agrarian Showcase</span>
            </button>

            <button
              onClick={() => setActiveTab('minerals')}
              className={`px-4 py-2 rounded-xl text-xs font-black uppercase tracking-wider transition-all cursor-pointer flex items-center gap-2 ${
                activeTab === 'minerals'
                  ? 'bg-[#D4AF37] text-[#3D1E16] shadow-lg border border-[#D4AF37]'
                  : 'bg-[#3D1E16] text-amber-200 hover:bg-[#9E472A]/40 border border-[#D4AF37]/30'
              }`}
            >
              <span>🪨</span>
              <span>The Mineral Pavilion</span>
            </button>

            <button
              onClick={() => setActiveTab('partnerships')}
              className={`px-4 py-2 rounded-xl text-xs font-black uppercase tracking-wider transition-all cursor-pointer flex items-center gap-2 ${
                activeTab === 'partnerships'
                  ? 'bg-[#D4AF37] text-[#3D1E16] shadow-lg border border-[#D4AF37]'
                  : 'bg-[#3D1E16] text-amber-200 hover:bg-[#9E472A]/40 border border-[#D4AF37]/30'
              }`}
            >
              <span>🤝</span>
              <span>Incentives & Guarantees</span>
            </button>
          </div>
        </div>
      </section>

      {/* Tab 1: The Agrarian Showcase */}
      {activeTab === 'agrarian' && (
        <section className="space-y-6 animate-fade-in">
          <div className="border-b border-[#D4AF37]/30 pb-3 flex items-center justify-between">
            <div>
              <span className="text-xs font-bold text-[#2E5A1C] uppercase tracking-widest bg-[#2E5A1C]/10 px-2.5 py-1 rounded-full border border-[#2E5A1C]/30">
                Pillar #1: Our Soil
              </span>
              <h2 className="text-2xl font-serif font-black text-slate-900 mt-1">
                The Agrarian Showcase & Agricultural Yields
              </h2>
            </div>
            <span className="text-xs font-mono text-emerald-800 bg-emerald-50 px-3 py-1 rounded-lg border border-emerald-200">
              Fertile Valleys of Uzairue
            </span>
          </div>

          <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
            {produceItems.map((item, idx) => (
              <div
                key={idx}
                className="bg-white rounded-2xl p-6 border-2 border-[#2E5A1C]/30 shadow-lg hover:shadow-xl transition-all space-y-4"
              >
                <div className="flex items-start justify-between gap-3">
                  <div className="flex items-center gap-3">
                    <span className="text-3xl p-2 bg-[#2E5A1C]/10 rounded-xl border border-[#2E5A1C]/30">
                      {item.icon}
                    </span>
                    <div>
                      <h3 className="font-serif font-black text-lg text-slate-900">
                        {item.title}
                      </h3>
                      <span className="text-[10px] font-black uppercase bg-[#2E5A1C] text-amber-100 px-2 py-0.5 rounded">
                        Annual Yield: {item.yield}
                      </span>
                    </div>
                  </div>
                </div>

                <p className="text-xs text-slate-700 leading-relaxed font-sans">
                  {item.description}
                </p>

                <div className="bg-emerald-50/80 p-3 rounded-xl border border-emerald-200 space-y-1 text-xs">
                  <span className="font-bold text-[#2E5A1C] block">💡 Investor Opportunity:</span>
                  <p className="text-slate-800">{item.opportunities}</p>
                </div>
              </div>
            ))}
          </div>
        </section>
      )}

      {/* Tab 2: The Mineral Pavilion */}
      {activeTab === 'minerals' && (
        <section className="space-y-6 animate-fade-in">
          <div className="border-b border-[#D4AF37]/30 pb-3 flex items-center justify-between">
            <div>
              <span className="text-xs font-bold text-[#9E472A] uppercase tracking-widest bg-[#9E472A]/10 px-2.5 py-1 rounded-full border border-[#9E472A]/30">
                Pillar #3: Our Wealth
              </span>
              <h2 className="text-2xl font-serif font-black text-slate-900 mt-1">
                The Mineral Pavilion • Kaolin Clay, Granite & Laterite
              </h2>
            </div>
            <span className="text-xs font-mono text-[#9E472A] bg-amber-50 px-3 py-1 rounded-lg border border-[#9E472A]/30">
              Emblem Labeled Deposits
            </span>
          </div>

          <div className="grid grid-cols-1 lg:grid-cols-3 gap-6">
            {mineralDeposits.map((min, idx) => (
              <div
                key={idx}
                className="bg-[#3D1E16] text-white rounded-2xl p-6 border-2 border-[#D4AF37]/50 shadow-xl space-y-4 flex flex-col justify-between"
              >
                <div className="space-y-3">
                  <div className="flex items-center justify-between">
                    <span className="text-3xl p-2 bg-[#2A140E] rounded-xl border border-[#D4AF37]/40">
                      {min.icon}
                    </span>
                    <span className="text-[10px] font-bold text-[#D4AF37] uppercase bg-[#2A140E] px-2 py-1 rounded border border-[#D4AF37]/30">
                      {min.reserveTag}
                    </span>
                  </div>

                  <h3 className="font-serif font-black text-xl text-[#D4AF37]">
                    {min.name}
                  </h3>

                  <p className="text-[11px] font-bold text-amber-200">
                    📍 Primary Deposit: {min.location}
                  </p>

                  <p className="text-xs text-amber-100/90 leading-relaxed font-sans">
                    {min.description}
                  </p>

                  <div className="space-y-1 bg-[#2A140E] p-3 rounded-xl border border-[#D4AF37]/30">
                    <span className="text-[10px] font-black uppercase text-[#D4AF37] block">
                      Industrial Applications:
                    </span>
                    <ul className="space-y-1 text-xs text-amber-100">
                      {min.applications.map((app, aIdx) => (
                        <li key={aIdx} className="flex items-center gap-1.5">
                          <span className="text-[#D4AF37]">•</span>
                          <span>{app}</span>
                        </li>
                      ))}
                    </ul>
                  </div>
                </div>

                <div className="pt-2 border-t border-[#D4AF37]/30">
                  <button
                    onClick={onOpenContactModal}
                    className="w-full py-2 bg-[#D4AF37] hover:bg-[#E67E22] text-[#3D1E16] hover:text-white rounded-xl text-xs font-black uppercase tracking-wider transition-all cursor-pointer"
                  >
                    Inquire for Prospecting Data
                  </button>
                </div>
              </div>
            ))}
          </div>
        </section>
      )}

      {/* Tab 3: Incentives & Guarantees */}
      {activeTab === 'partnerships' && (
        <section className="bg-white rounded-2xl p-6 sm:p-8 border-2 border-[#D4AF37]/40 shadow-xl space-y-6 animate-fade-in">
          <div className="space-y-2">
            <span className="text-xs font-black uppercase text-[#3D1E16] tracking-widest bg-[#D4AF37]/20 px-3 py-1 rounded-full">
              Royal Endorsement & Royal Protections
            </span>
            <h2 className="text-2xl font-serif font-black text-slate-900">
              Kingdom Guarantees for Investors & Partners
            </h2>
            <p className="text-xs text-slate-600">
              Under the auspices of His Royal Highness Ogieneni of Uzairue, we offer peace, security, and institutional goodwill to investors.
            </p>
          </div>

          <div className="grid grid-cols-1 md:grid-cols-3 gap-6">
            <div className="p-5 bg-amber-50/60 rounded-xl border border-[#D4AF37]/40 space-y-2">
              <span className="text-3xl">🕊️</span>
              <h3 className="font-serif font-bold text-[#3D1E16]">Guaranteed Peace & Security</h3>
              <p className="text-xs text-slate-700">
                Uzairue Vigilante Corps and Royal Council ensure peaceful coexistence, zero community disruption, and safe operations.
              </p>
            </div>

            <div className="p-5 bg-amber-50/60 rounded-xl border border-[#D4AF37]/40 space-y-2">
              <span className="text-3xl">📜</span>
              <h3 className="font-serif font-bold text-[#3D1E16]">Palace Memorandum of Understanding</h3>
              <p className="text-xs text-slate-700">
                Direct Royal Charter protection for land acquisition, leaseholds, and community social responsibility alignment.
              </p>
            </div>

            <div className="p-5 bg-amber-50/60 rounded-xl border border-[#D4AF37]/40 space-y-2">
              <span className="text-3xl">🏭</span>
              <h3 className="font-serif font-bold text-[#3D1E16]">Infrastructure & Power Access</h3>
              <p className="text-xs text-slate-700">
                Close proximity to Auchi-Jattu expressways, power grids, and regional transport networks connecting Edo, Abuja, and Lagos.
              </p>
            </div>
          </div>
        </section>
      )}

    </div>
  );
};
